import { useCallback, useEffect, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import type { SelectChangeEvent } from "@mui/material";
import { ListItemText, MenuItem, Typography } from "@mui/material";
import {
  IconListItemText,
  InputRow,
  PhoneInputContainer,
  PhoneInputWrapper,
  PhonePrefixSelect,
  StyledInfoIcon,
} from "../../styles/styled";
import { InputLabel } from "@mekidoc/ui";
import { ClickableTooltip, SingleFormInput } from "../../components";
import { replaceCurrentPhoneCode, trimString } from "../../utils";
import phoneCodes from "../../data/phoneCodes.json";

interface PhoneOwner extends FieldValues {
  phone: string;
}

interface PhoneCodeProps {
  name: string;
  icon: string;
  prefix: string;
}

interface PhoneCodeState {
  currentPhoneCode: PhoneCodeProps;
  setCurrentPhoneCode: React.Dispatch<React.SetStateAction<PhoneCodeProps>>;
}

interface PhoneInputProps<T extends PhoneOwner> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  clearErrors: UseFormClearErrors<T>;
  phoneCodeState: PhoneCodeState;
  showTooltip?: boolean;
  obscureInTracker?: boolean;
}

export const PhoneInput = <T extends PhoneOwner>({
  register,
  errors,
  control,
  setValue,
  trigger,
  clearErrors,
  phoneCodeState: { currentPhoneCode, setCurrentPhoneCode },
  showTooltip = false,
  obscureInTracker = false,
}: PhoneInputProps<T>) => {
  const phone = useWatch({ control, name: "phone" as Path<T> });
  const phoneCodeList = phoneCodes.phoneCodes;
  const [showCurrentPhoneCode, setShowCurrentPhoneCode] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const changeTooltipOpenState = () => setTooltipOpen((prev) => !prev);

  const replaceCurrentPhoneCodeCallback = useCallback(
    (phone: string, currentPhoneCodePrefix: string) => {
      const currentPhoneValue = replaceCurrentPhoneCode(phone, currentPhoneCodePrefix);

      setValue("phone" as Path<T>, currentPhoneValue as PathValue<T, Path<T>>);
    },
    [setValue],
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const selectedPhoneCode = event.target.value as string;

      const newPhoneCode = {
        name: "",
        icon: selectedPhoneCode.split(" ")[0],
        prefix: selectedPhoneCode.split(" ")[1],
      };

      setCurrentPhoneCode(newPhoneCode);
      replaceCurrentPhoneCodeCallback(phone, newPhoneCode.prefix);
    },
    [phone, setCurrentPhoneCode, replaceCurrentPhoneCodeCallback],
  );

  // The prefix select hides when the phone empties, and shows when the phone starts to
  // look like a number with its country code. Adjusted during render, when the phone
  // changes, rather than in an effect, so no render shows the select out of date.
  const [prevPhone, setPrevPhone] = useState<string | undefined>(undefined);

  // A new object per detection, so detecting the same code again is applied again.
  const [detection, setDetection] = useState<{ code: PhoneCodeProps } | null>(null);

  if (phone !== prevPhone) {
    setPrevPhone(phone);

    if (!phone) {
      setShowCurrentPhoneCode(false);
    } else if (
      /^\+?\d+$/.test(phone) &&
      !showCurrentPhoneCode &&
      ((phone.length >= 3 && !phone.startsWith("+")) ||
        (phone.length >= 4 && phone.startsWith("+")))
    ) {
      setShowCurrentPhoneCode(true);
      setDetection({
        code:
          phoneCodeList.find((code) => phone.startsWith(code.prefix)) ?? currentPhoneCode,
      });
    }
  }

  // A detected code goes to the parent's state, and out of the phone's value, once.
  const handledDetectionRef = useRef<{ code: PhoneCodeProps } | null>(null);

  useEffect(
    function ApplyDetectedPhoneCode() {
      if (!detection || handledDetectionRef.current === detection) return;

      handledDetectionRef.current = detection;
      setCurrentPhoneCode(detection.code);
      replaceCurrentPhoneCodeCallback(phone, detection.code.prefix);
    },
    [detection, phone, setCurrentPhoneCode, replaceCurrentPhoneCodeCallback],
  );

  return (
    <PhoneInputWrapper>
      <InputRow>
        <InputLabel>Número de teléfono</InputLabel>
        {showTooltip && (
          <ClickableTooltip
            title={
              "Lo utilizarás para iniciar sesión y si necesitas soporte técnico. No lo compartiremos con tus pacientes"
            }
            open={tooltipOpen}
            setOpen={setTooltipOpen}
          >
            <StyledInfoIcon onClick={changeTooltipOpenState} />
          </ClickableTooltip>
        )}
      </InputRow>
      <PhoneInputContainer>
        {showCurrentPhoneCode && (
          <PhonePrefixSelect
            variant="outlined"
            size="small"
            value={currentPhoneCode.icon + " " + currentPhoneCode.prefix}
            renderValue={(selected) => {
              // The value this Select is given: the code's icon and prefix.
              return selected as string;
            }}
            onChange={handleSelectChange}
          >
            {phoneCodeList?.map((option) => (
              <MenuItem value={`${option.icon} ${option.prefix}`} key={option.name}>
                <IconListItemText>{option.icon}</IconListItemText>
                <ListItemText>{trimString(option.name, 25)}</ListItemText>
                <Typography variant="body2" color="text.secondary">
                  {option.prefix}
                </Typography>
              </MenuItem>
            ))}
          </PhonePrefixSelect>
        )}

        <SingleFormInput
          parameterName={"phone" as Path<T>}
          errorText="Debes escribir tu número de teléfono."
          register={register}
          errors={errors}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            trigger("phone" as Path<T>);
            setValue(
              "phone" as Path<T>,
              event.target.value.replace(/\s/g, "") as PathValue<T, Path<T>>,
            );
          }}
          validate={(value) => {
            const phoneRegex = /^\+?[0-9 ]+$/;

            if (!phoneRegex.exec(value) && value !== "+") {
              return "El teléfono solo puede contener números";
            }

            const phoneWithoutPrefix = value
              .replace(currentPhoneCode.prefix, "")
              .replace(/\s/g, "");

            if (phoneWithoutPrefix.length < 9) {
              return "Teléfono incompleto";
            }

            if (phoneWithoutPrefix.length > 9) {
              return "Teléfono muy largo";
            }

            clearErrors("phone" as Path<T>);

            return true;
          }}
          obscureInTracker={obscureInTracker}
        />
      </PhoneInputContainer>
    </PhoneInputWrapper>
  );
};
